import { useState } from "react";
import { Form, FormInput, FormActions, FormSelectList } from "../Controls/Form";
import UserData from "../Controls/UserData";
import EmploymentSelectionListItem from "../ListItems/EmploymentSelectionListItem";
import Help from "../Controls/Help";
import CustomButton from "../Controls/CustomButton";
import { toast } from "react-toastify";
import API from "@src/API";
import Helpers from "../Controls/helpers";

function emptyConnection() {
  return {
    ID: 0,
    FirstName: "",
    LastName: "",
    Phone: "",
    Email: "",
    Employments: [],
    Status: "",
  };
}

/**
 * Connection form body. Prefer ConnectionEditModal for UI entry points.
 */
export default function ConnectionEdit({ connectionId = 0, onClose }) {
  const id = Number.parseInt(connectionId, 10) || 0;
  const [userData] = useState(UserData.UserData() || {});
  const [connection, updateConnection] = useState(() => {
    if (!id) return emptyConnection();
    const found = (userData.connections || []).find((i) => Number(i.ID ?? i.id) === id);
    if (!found) return emptyConnection();
    return {
      ...found,
      Employments: found.Employments || found.employments || [],
    };
  });
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("details");

  function close() {
    onClose?.();
  }

  async function onDoneAsync() {
    if (busy) return;
    setBusy(true);
    try {
      const ret = await UserData.UpdateAsync("connections", connection, true);
      if (ret?.ok || ret?.result === "Success") {
        toast.success("Connection saved");
        await UserData.GetUserDataAsync();
        close();
      } else {
        toast.error(ret?.message || "Could not save connection");
      }
    } catch (err) {
      toast.error("Error: " + err);
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    const connIdLocal = connection.ID || connection.id;
    if (!connIdLocal) {
      close();
      return;
    }
    const resp = await UserData.DeleteAsync("connections", connection);
    if (resp?.result) {
      toast.success("Connection deleted");
      await UserData.GetUserDataAsync();
      close();
    } else {
      toast.error(resp?.error || "Delete failed");
    }
  }

  const isEmploymentSelected = (empId) => {
    const id = Number(empId);
    return (connection.Employments || []).some((x) => Number(x) === id);
  };

  const status = connection?.Status ?? connection?.status;
  const statusLabel = Helpers.connectionStatusLabel(status);
  const connId = connection?.ID ?? connection?.id;

  const accept = async () => {
    const resp = await API.postAsync(`/Connection/${connId}/Accept`, {});
    if (resp && resp.ok) {
      toast.success("Connection accepted");
      updateConnection({ ...connection, Status: "Verified", status: "Verified" });
      await UserData.GetUserDataAsync();
    } else {
      toast.error(resp?.jsonData?.message || "Accept failed");
    }
  };

  const decline = async () => {
    const resp = await API.postAsync(`/Connection/${connId}/Decline`, {});
    if (resp && resp.ok) {
      toast.success("Connection declined");
      await UserData.GetUserDataAsync();
      close();
    } else {
      toast.error(resp?.jsonData?.message || "Decline failed");
    }
  };

  const onToggleAllEmployments = () => {
    const history = userData.employmentHistory || [];
    const selected = connection.Employments || [];
    const allSelected =
      history.length > 0 &&
      history.every((emp) => selected.some((x) => Number(x) === Number(emp.ID)));
    updateConnection({
      ...connection,
      Employments: allSelected ? [] : history.map((emp) => emp.ID),
    });
  };

  const onClickEmployment = (empID) => {
    const id = Number(empID);
    updateConnection((prevConnection) => {
      const currentEmployments = prevConnection.Employments || [];
      const isSelected = currentEmployments.some((x) => Number(x) === id);
      const updatedEmployments = isSelected
        ? currentEmployments.filter((x) => Number(x) !== id)
        : [...currentEmployments, id];
      return { ...prevConnection, Employments: updatedEmployments };
    });
  };

  if (id > 0 && !(connection.ID || connection.id)) {
    return (
      <div className="cm-modal-embedded">
        <div className="cm-modal-body-pad">
          <p className="fieldHint">Connection not found.</p>
          <CustomButton type="button" variant="contained" color="primary" onClick={close}>
            Close
          </CustomButton>
        </div>
      </div>
    );
  }

  const isPotential = Helpers.isConnectionPotential(status);
  const history = userData.employmentHistory || [];
  const sharedCount = (connection.Employments || []).length;

  return (
    <div className="cm-modal-embedded">
      <Form className="cm-modal-form" object={connection} onUpdate={updateConnection}>
        <div className="cm-modal-tabs" role="tablist" aria-label="Connection sections">
          <button
            type="button"
            role="tab"
            id="connection-tab-details"
            aria-selected={tab === "details"}
            aria-controls="connection-panel-details"
            className={tab === "details" ? "is-active" : undefined}
            data-testid="connection-tab-details"
            onClick={() => setTab("details")}
          >
            Details
          </button>
          <button
            type="button"
            role="tab"
            id="connection-tab-workspaces"
            aria-selected={tab === "workspaces"}
            aria-controls="connection-panel-workspaces"
            className={tab === "workspaces" ? "is-active" : undefined}
            data-testid="connection-tab-workspaces"
            onClick={() => setTab("workspaces")}
          >
            Shared Workspaces{sharedCount > 0 ? ` (${sharedCount})` : ""}
          </button>
        </div>

        <div className="cm-modal-tab-panels">
          <div
            id="connection-panel-details"
            role="tabpanel"
            aria-labelledby="connection-tab-details"
            aria-hidden={tab !== "details"}
            className={`cm-modal-tab-panel${tab === "details" ? " is-active" : ""}`}
            {...(tab !== "details" ? { inert: "" } : {})}
          >
            <Help id="2141" />
            {statusLabel ? (
              <p className="fieldHint" data-testid="connection-status">
                Status: {statusLabel}
              </p>
            ) : null}
            {isPotential ? (
              <div className="field form-group connection-accept-actions">
                <CustomButton type="button" variant="contained" color="primary" onClick={accept}>
                  Accept
                </CustomButton>
                <CustomButton type="button" variant="contained" color="error" onClick={decline}>
                  Decline
                </CustomButton>
              </div>
            ) : null}
            <FormInput label="First Name" field="FirstName" required />
            <FormInput label="Last Name" field="LastName" required />
            <FormInput type="email" label="Email" field="Email" required />
            <FormInput label="Phone Number" field="Phone" />
          </div>

          <div
            id="connection-panel-workspaces"
            role="tabpanel"
            aria-labelledby="connection-tab-workspaces"
            aria-hidden={tab !== "workspaces"}
            className={`cm-modal-tab-panel${tab === "workspaces" ? " is-active" : ""}`}
            {...(tab !== "workspaces" ? { inert: "" } : {})}
          >
            <p className="fieldHint">
              Select the workplaces you and this person had or have in common.
            </p>
            <FormSelectList
              field="Employments"
              label={
                sharedCount + " of " + history.length + " selected"
              }
              defaultShowOnlySelected={false}
              options={[...history].sort((a, b) =>
                (b.StartDate || "").localeCompare(a.StartDate || "")
              )}
              onToggleSelect={onToggleAllEmployments}
              renderItem={(employment) => (
                <EmploymentSelectionListItem
                  key={employment.ID}
                  employment={employment}
                  employers={userData.employers || []}
                  selected={isEmploymentSelected(employment.ID)}
                  onClick={() => onClickEmployment(employment.ID)}
                />
              )}
            />
          </div>
        </div>

        <FormActions
          doneLabel={busy ? "Saving…" : "Save"}
          onCancelAsync={async () => {
            close();
          }}
          onDeleteAsync={
            (connection.ID || connection.id) > 0
              ? async () => {
                  await onDelete();
                }
              : undefined
          }
          onSaveAsync={async () => {
            await onDoneAsync();
          }}
        />
      </Form>
    </div>
  );
}
